import * as React from "react"
import { useRef } from "react"
import "./crs-selector.css"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { InlineTextInput } from "@nova/component-inline-text-input"
import classNames from "classnames"
import { useCRSSelectorState } from "./use-crs-selector-state"
import { useTranslation } from "@nova/use-translation"
import { ClickAwayListener } from "@nova/component-click-away-listener"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import * as Icons from "@nova/component-icons"

export type Notation = {
    id: string
    label: string
    getTick: ([lng, lat]: number[]) => string[]
}

type CRSSelectorProps = {
    onSelect: (crs: CRS | null) => void
    allCRSs: CRS[]
    label?: string
    className?: string
    disabled?: boolean
    defaultCRSId?: string
}

export type CRS = {
    proj4: string
    notations: Record<string, Notation>
    code: string
    name: string
}

export const CRSSelector = ({
    onSelect,
    allCRSs,
    label,
    className,
    disabled,
    defaultCRSId,
}: CRSSelectorProps) => {
    const {
        CRSs,
        setCRSs,
        inputValue,
        setInputValue,
        dropdownOpen,
        setDropdownOpen,
        hovered,
        setHovered,
        scrollRef,
        selectedCRS,
        setSelectedCRS,
    } = useCRSSelectorState(allCRSs, defaultCRSId)

    const inputRef = useRef<HTMLInputElement>(null)
    const { t } = useTranslation()

    const closeMenu = () => {
        setDropdownOpen(false)
        setHovered(-1)
    }
    const selectCRS = (crs: CRS) => {
        setDropdownOpen(false)
        setInputValue(crs.name)
        setSelectedCRS(crs)
        onSelect(crs)
    }

    const filter = (searchTerm: string) => {
        setInputValue(searchTerm)
        setDropdownOpen(true)
        if (!searchTerm) {
            setCRSs(allCRSs)
            return
        }
        const getPoints = (crs: CRS) => {
            if (searchTerm.toLowerCase() === crs.name.toLowerCase() || searchTerm === crs.code)
                return 1000
            else if (crs.name.toLowerCase().startsWith(searchTerm.toLowerCase()))
                return 100 - crs.name.length
            else return -crs.name.length
        }
        const searchTokens = searchTerm.split(" ")
        const filtered = allCRSs
            .filter(
                (crs) =>
                    searchTokens.every((token) =>
                        crs.code.toLowerCase().includes(token.toLowerCase())
                    ) ||
                    searchTokens.every((token) =>
                        crs.name.toLowerCase().includes(token.toLowerCase())
                    )
            )
            .sort((a, b) => getPoints(b) - getPoints(a))
            .slice(0, 100)
        setCRSs(filtered)
    }

    const clearSelector = () => {
        filter("")
        setSelectedCRS(allCRSs.find((crs) => crs.name === defaultCRSId) || null)
        onSelect(null)
        closeMenu()
    }

    useEffectUnsafe(() => {
        if (defaultCRSId === selectedCRS?.name) return

        const defaultCRS = allCRSs.find((crs) => crs.name === defaultCRSId)
        if (!defaultCRS) {
            clearSelector()
            return
        }
        selectCRS(defaultCRS)
    }, [defaultCRSId])

    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.code === "ArrowDown" || e.code === "ArrowUp" || e.code === "Tab") e.preventDefault()
        const setNewHovered = (newHovered: number) => {
            const itemHeight = 42
            const visibleItems = 4
            if (scrollRef.current) {
                const currentScroll = scrollRef.current.scrollTop
                if (newHovered * itemHeight >= currentScroll + 4 * itemHeight)
                    scrollRef.current.scrollTop = Math.round(
                        itemHeight * (newHovered - (visibleItems - 1))
                    )
                else if (newHovered * itemHeight <= currentScroll)
                    scrollRef.current.scrollTop = itemHeight * newHovered
            }
            setHovered(newHovered)
        }
        if (e.code === "ArrowUp" || (e.code === "Tab" && e.shiftKey)) {
            setNewHovered(Math.max(hovered - 1, 0))
        } else if (e.code === "ArrowDown" || e.code === "Tab") {
            setNewHovered(Math.min(hovered + 1, CRSs.length - 1))
        } else if ((e.code === "Enter" || e.code === "Space") && hovered !== -1) {
            selectCRS(CRSs[hovered])
            closeMenu()
        } else if (e.code === "Escape") {
            closeMenu()
            ;(e.target as HTMLElement).blur()
        }
    }

    return (
        <div>
            {label && <div className="crs-selector__label">{t(label)}</div>}
            <ClickAwayListener
                className={classNames(
                    "crs-selector__container",
                    { "border border-blue-200": dropdownOpen },
                    className
                )}
                onKeyDown={onKeyDown}
                role="combobox"
                onClickAway={closeMenu}
                onClick={() => {
                    inputRef.current?.focus()
                    filter(inputValue)
                }}
            >
                <InlineTextInput
                    ref={inputRef}
                    onClick={(e) => (dropdownOpen ? e.stopPropagation() : {})}
                    name="search"
                    handleChange={filter}
                    className="flex-1"
                    inputClassName="crs-selector__input placeholder:text-gray-300 text-[14px] !border-none !outline-none focus:!outline-none focus:!ring-0 focus:!border-transparent"
                    value={inputValue}
                    autoComplete="off"
                    placeholder={t("Enter a name or code")}
                    disabled={disabled}
                />
                <div
                    className="absolute right-2 top-[50%] -translate-y-[50%]"
                    onClick={(e) => (dropdownOpen ? e.stopPropagation() : {})}
                >
                    <Icons.ArrowDropDownIcon
                        dropdownOpen={dropdownOpen}
                        setDropdownOpen={setDropdownOpen}
                        disabled={disabled}
                    />
                </div>
                {dropdownOpen && CRSs.length >= 1 && (
                    <ScrollableArea
                        className="crs-selector__dropdown !bg-gray-700 mt-0.5"
                        onKeyDown={(e) => e.preventDefault()}
                        ref={scrollRef}
                        data-testid="crs-selector-scroll-area"
                        role="listbox"
                    >
                        {CRSs.map((crs, idx) => {
                            const { name } = crs
                            return (
                                <div
                                    role="option"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        selectCRS(crs)
                                    }}
                                    className={classNames("crs-selector__dropdown__item", {
                                        "crs-selector__dropdown__item-focused": hovered === idx,
                                    })}
                                    key={idx}
                                    tabIndex={0}
                                >
                                    <div className="crs-selector__dropdown__name">{name}</div>
                                </div>
                            )
                        })}
                    </ScrollableArea>
                )}
            </ClickAwayListener>
        </div>
    )
}
